import React from 'react';
import { oohData } from '../utils/oohData';
import { NetworkIcon, LinkIcon, LayoutIcon, AlertTriangleIcon } from 'lucide-react';
type Level = 'low' | 'moderate' | 'high';
const LEVEL_STYLES: Record<Level, {
  chip: string;
  dot: string;
  filled: number;
}> = {
  low: {
    chip: 'bg-green-100 text-green-800 border-green-200',
    dot: 'bg-green-500',
    filled: 1
  },
  moderate: {
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    filled: 2
  },
  high: {
    chip: 'bg-red-100 text-red-800 border-red-200',
    dot: 'bg-red-500',
    filled: 3
  }
};
const RISK_ICONS = [LinkIcon, LayoutIcon, AlertTriangleIcon];
const SeverityMeter: React.FC<{
  level: Level;
}> = ({
  level
}) => {
  const {
    dot,
    filled
  } = LEVEL_STYLES[level];
  return <div className="flex items-center space-x-1" aria-hidden="true" data-id="element-276" data-mp="1tx25dbrmkcoo">
      {[0, 1, 2].map(i => <span key={i} className={`w-5 h-1.5 rounded-full ${i < filled ? dot : 'bg-gray-200'}`} data-id="element-277" data-mp="1xz6ym9e5v7as" />)}
    </div>;
};
export const SystemDynamics: React.FC = () => {
  const {
    interactionSummary,
    risks
  } = oohData.systemDynamics;
  return <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-278" data-mp="cerph1xovvp4">
      <div className="flex items-center space-x-3 mb-3" data-id="element-279" data-mp="ce8a9szfbles">
        <div className="p-2 bg-[#1E90FF]/10 text-[#1E90FF] rounded-lg" data-id="element-280" data-mp="7f54257pj958">
          <NetworkIcon className="w-5 h-5" data-id="element-281" data-mp="7i13xz3487wb" />
        </div>
        <h2 className="text-lg font-medium text-gray-800" data-id="element-282" data-mp="3fw9nyirknqcv">
          How the Drivers Interact
        </h2>
      </div>

      {/* Interaction narrative — the "why" behind the risks below */}
      <p className="text-sm text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-283" data-mp="3pupi6ulwbfql">
        {interactionSummary}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-284" data-mp="3kt0393vjyhx8">
        {risks.map((risk, idx) => {
        const Icon = RISK_ICONS[idx] ?? AlertTriangleIcon;
        const styles = LEVEL_STYLES[risk.level];
        return <div key={risk.label} className="p-5 rounded-xl border border-gray-100 bg-gray-50/50 flex flex-col" data-id="element-285" data-mp="106oottjycv9c" data-mpi={idx}>
              {/* Header: icon + title */}
              <div className="flex items-center space-x-3 mb-3" data-id="element-286" data-mp="1c8gciw580ld0">
                <div className="p-2 bg-white border border-gray-100 text-gray-500 rounded-lg" data-id="element-287" data-mp="1h69hjlusf9bp">
                  <Icon className="w-5 h-5" data-id="element-288" data-mp="1m4ayo18nkykj" />
                </div>
                <h3 className="font-semibold text-gray-900 leading-tight" data-id="element-289" data-mp="ohkl404ofs6a">
                  {risk.label}
                </h3>
              </div>

              {/* Severity: badge + meter */}
              <div className="flex items-center justify-between mb-3" data-id="element-290" data-mp="yep1fjbuctf6">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${styles.chip}`} data-id="element-291" data-mp="yddvbcuxb9kw">
                  {risk.level}
                </span>
                <SeverityMeter level={risk.level} data-id="element-292" data-mp="ap4dsn4qt7zk" />
              </div>

              {/* Plain-language definition */}
              <p className="text-xs font-medium text-gray-500 italic mb-2" data-id="element-293" data-mp="2nyw375gxdovd">
                {risk.definition}
              </p>

              {/* The reason */}
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-294" data-mp="2eyzc6x72cdx5">
                {risk.reason}
              </p>

              {/* Hard evidence from the attention heatmap */}
              {'evidence' in risk && risk.evidence && <p className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 leading-relaxed" data-id="element-295" data-mp="251uzdnz3xut0">
                  <span className="font-semibold text-gray-800" data-id="element-296" data-mp="209jpbsanksmq">
                    Evidence:{' '}
                  </span>
                  {risk.evidence}
                </p>}

              {/* Keeps each risk constructive by naming the fix */}
              {'mitigation' in risk && risk.mitigation && <p className="mt-2 text-xs font-medium text-[#1E90FF]" data-id="element-297" data-mp="1mcoblmjgid0c">
                  {risk.mitigation}
                </p>}
            </div>;
      })}
      </div>
    </div>;
};